import { createBrowserRouter, Params } from "react-router-dom";
import store from "./store";
import React from "react";
import {
  rapidstorApi,
  selectLocationBySlug,
} from "./features/rapidstor/rapidstorApiSlice";
import Root from "./routes/Root";
import Error from "./routes/Error";
import LocationList from "./routes/LocationList";
import Quote from "./routes/Quote";
import BookUnit from "./routes/BookUnit";
import Location from "./features/rapidstor/entities/Location";
import BookConfirmed from "./routes/BookConfirmed";
import BookConfirm from "./routes/BookConfirm";
import UnitSelector from "./routes/UnitSelector";
import Callback from "./routes/Callback";

interface LocationParams {
  locationSlug: string;
}

// interface OptionalLocationParams {
//   locationSlug?: string;
// }

type Destination = string | number | (() => void);

/**
 * Get the destination to go back to when the back button is pressed.
 *
 * Returns one of the following:
 * string: a URL to navigate to
 * number: relative movement through browser history stack
 * function: a function to call
 *
 * @param path
 * @param location
 */
export function getBackDestination(path: string, location: Location | null): Destination {
  if (!location) {
    return "/";
  }

  if (path === "/booking") {
    return `/locations/${location.slug}`;
  } else if (path.match(/^\/locations\/[^/]+\/?$/)) {
    // When in the questionnaire, go back through the steps instead of navigating back
    // immediately. The stack length must be at least 2, because the last element in the
    // stack is the current step.
    return `/locations/${location.slug}`;
  } else if (path.match(/^\/locations\/[^/]+\/units\/?$/)) {
    // When in the unit listing, go back to the questionnaire
    return `/locations/${location.slug}`;
  } else if (path.match(/^\/locations\/[^/]+\/(book|reserve)\/?$/)) {
    return `/locations/${location.slug}/units`;
  } else if (path === "/contact" || path.match(/\/quote-sent\/?$/)) {
    // Treat the contact page as almost a separate drawer. Closing the contact page will
    // always go back to what you were looking at before you opened it, and using the
    // go back button will never return you to the contact page.
    return -1;
  } else {
    return "/";
  }
}

const locationLoader = async ({ params }: { params: Params<keyof LocationParams> }) => {
  const promise = store.dispatch(rapidstorApi.endpoints.getLocations.initiate());

  try {
    await promise.unwrap();
    const location = selectLocationBySlug(
      store.getState(),
      params.locationSlug as string
    );

    if (!location) {
      throw new Response("", {
        status: 404,
        statusText: "Not Found",
      });
    }

    return location;
  } finally {
    promise.unsubscribe();
  }
};

// const optionalLocationLoader = async ({
//   params,
// }: {
//   params: Params<keyof OptionalLocationParams>;
// }) => {
//   if (params.locationSlug) {
//     return await locationLoader({ params });
//   }

//   return null;
// };

export default createBrowserRouter(
  [
    {
      path: "/",
      element: <Root />,
      errorElement: <Error />,
      children: [
        // {
        //   path: "",
        //   loader: () => {
        //     return redirect("locations");
        //   },
        // },
        {
          path: "",
          element: <LocationList />,
          errorElement: <Error />,
        },
        {
          path: ":locationSlug",
          element: <UnitSelector />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: ":locationSlug/book",
          element: <BookUnit />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: ":locationSlug/quote/:reservationTokenParams?",
          element: <Quote />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: ":locationSlug/confirm/:reservationTokenParams?",
          element: <BookConfirm />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: ":locationSlug/confirmed/:reservationTokenParams",
          element: <BookConfirmed />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: ":locationSlug/confirmed-with-error/:reservationTokenParams",
          element: <BookConfirmed withError />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: ":locationSlug/callback",
          element: <Callback />,
          loader: locationLoader,
          errorElement: <Error />,
        },
      ],
    },
  ],
  {
    basename: import.meta.env.PROD ? "/storage" : "/",
  }
);
